import { useRef, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import { useAuth } from '../../contexts/AuthContext';
import { buildApiUrl } from '../../utils/api';
import {
  ACCEPTED_AVATAR_TYPES,
  downscaleImageFile,
  validateAvatarFile,
} from '../../utils/imageDownscale';
import { PersonAvatar } from './PersonAvatar';

/** Where the avatar is written: the caller's own account, or an employee record. */
export type AvatarUploadTarget =
  | { kind: 'self' }
  | { kind: 'employee'; employeeId: string };

export interface AvatarUploadProps {
  /** Person name — drives the preview initials fallback. */
  name: string;
  /** Current owned-avatar URL (`/api/images/:id`) if any. */
  avatarUrl?: string | null;
  /** Which record this uploads to. */
  target: AvatarUploadTarget;
  /** Called with the new avatarUrl after upload, or `null` after removal. */
  onChange?: (avatarUrl: string | null) => void;
  /** Preview diameter (px). */
  size?: number;
}

function endpointFor(target: AvatarUploadTarget): string {
  return target.kind === 'self'
    ? '/api/users/me/avatar'
    : `/api/employees/${target.employeeId}/avatar`;
}

/**
 * Avatar upload / replace / remove control (AC-23, AC-E5). Validates + downscales
 * client-side, then PUTs multipart to the self or employee endpoint; surfaces
 * rejects as inline error text rather than a toast. Used in profile settings
 * (self) and the employee edit dialog (employee).
 */
export function AvatarUpload({
  name,
  avatarUrl,
  target,
  onChange,
  size = 72,
}: AvatarUploadProps) {
  const { getAccessToken } = useAuth();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Track the URL locally so the preview updates immediately after a change,
  // even before the parent re-renders with fresh data.
  const [currentUrl, setCurrentUrl] = useState<string | null>(avatarUrl ?? null);

  const authHeader = (): Record<string, string> => {
    const token = getAccessToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const pickFile = () => {
    setError(null);
    inputRef.current?.click();
  };

  const handleFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset the input so re-picking the same file still fires onChange.
    event.target.value = '';
    if (!file) return;

    const validationError = validateAvatarFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const blob = await downscaleImageFile(file);
      const formData = new FormData();
      formData.append('file', blob, 'avatar.webp');

      // NOTE: do NOT set Content-Type — the browser adds the multipart boundary.
      const response = await fetch(buildApiUrl(endpointFor(target)), {
        method: 'PUT',
        headers: authHeader(),
        body: formData,
      });
      if (!response.ok) {
        const message = await response
          .json()
          .then((body) => body?.message as string | undefined)
          .catch(() => undefined);
        throw new Error(message ?? 'Upload failed. Please try again.');
      }
      const { avatarUrl: newUrl } = (await response.json()) as { avatarUrl: string };
      setCurrentUrl(newUrl);
      onChange?.(newUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    setBusy(true);
    setError(null);
    try {
      const response = await fetch(buildApiUrl(endpointFor(target)), {
        method: 'DELETE',
        headers: authHeader(),
      });
      if (!response.ok && response.status !== 204) {
        throw new Error('Could not remove the picture. Please try again.');
      }
      setCurrentUrl(null);
      onChange?.(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not remove the picture.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Box sx={{ position: 'relative', flexShrink: 0 }}>
        <PersonAvatar name={name} src={currentUrl} size={size} />
        {busy && (
          <CircularProgress
            size={size * 0.5}
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              mt: `${-size * 0.25}px`,
              ml: `${-size * 0.25}px`,
            }}
          />
        )}
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<PhotoCameraIcon fontSize="small" />}
            onClick={pickFile}
            disabled={busy}
          >
            {currentUrl ? 'Replace' : 'Upload'}
          </Button>
          {currentUrl && (
            <Button
              size="small"
              color="inherit"
              startIcon={<DeleteOutlineIcon fontSize="small" />}
              onClick={handleRemove}
              disabled={busy}
              sx={{ color: 'text.secondary' }}
            >
              Remove
            </Button>
          )}
        </Box>
        {error ? (
          <Typography
            role="alert"
            sx={{ mt: 0.75, fontSize: '0.75rem', color: (theme) => theme.palette.statusInk.danger }}
          >
            {error}
          </Typography>
        ) : (
          <Typography sx={{ mt: 0.75, fontSize: '0.72rem', color: 'text.disabled' }}>
            PNG, JPEG or WebP · up to 5 MB
          </Typography>
        )}
      </Box>

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_AVATAR_TYPES.join(',')}
        onChange={handleFile}
        hidden
        aria-hidden="true"
      />
    </Box>
  );
}

export default AvatarUpload;
